/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import RenderHTML from 'react-native-render-html';
import { isWeb, fullWidth } from '@global/screens';
import { CHECK_SIZE } from '@/constants';
import Colors from '@global/colors';
import {
  getSavedFilter,
  hasProductBadge,
  splitProductByCombination,
  convertProductMarketType,
  firstLetter,
  isVn,
  capitalizeText,
  exportCsvApi,
  exportPdf,
} from '@global/helpers';
import Image from '@components/global/Image';
import SociollaText from '@components/sociolla/SociollaText';
import SelectBox from '@components/form/SelectBox';
import ModalHalf from '@components/modal/ModalHalf';
import ProductBadge from '@components/global/ProductBadge';
import Table from '@components/table/Table';
// import TableMobile from '@components/table/TableMobile';
import ListProductBrand from '@/screens/catalog/components/ListProductBrand';
import ImageContain from '@components/global/ImageContain';
import * as Layout from '@components/screen/Layout';
import ModalDetailProduct from '@components/transaction/catalog/ModalDetailProduct';
import ProductItemMobile from '@/screens/catalog/components/ProductItemMobile';

import useApi from '@/hooks/useApi';
import { MsCatalog } from '@api/ms-catalog';
import store from '@/store';
import { setProductData } from '@/store/product';
import { paramsReseted, paginationAdded } from '@/store/params';
import { showPopup } from '@/store/modal';

const getCategoryPath = (product) => {
  const availableCategories = Array.isArray(product?.categories)
    ? product.categories.filter((category) => category?.id)
    : [];

  if (!availableCategories.length) {
    return '';
  }

  const visibleCategories = availableCategories.filter(
    (category) => !category.is_show_category && !category.is_promotion,
  );

  const categoriesForPath = visibleCategories.length
    ? visibleCategories
    : availableCategories;

  const categoryPath = categoriesForPath
    .slice(0, 3)
    .map((category) => category?.name?.trim())
    .filter(Boolean)
    .join('/');

  return categoryPath || '';
};

const tableStructure = {
  image: {
    head: 'Product Image',
    body: 'image',
    type: 'image',
  },
  reference: {
    head: 'Internal Reference',
    body: 'reference',
    type: 'slot',
  },
  name: {
    head: 'Name',
    body: 'name',
  },
  variant: {
    head: 'Attribute Value',
    body: 'variant',
  },
  category: {
    head: 'Category',
    body: 'categoryPath',
  },
  price: {
    head: 'Price',
    body: 'price',
    type: 'currency',
  },
  attribute: {
    head: 'Attribute',
    body: 'attribute',
    type: 'slot',
  },
};

const SORTS = [
  {
    label: isWeb ? 'All Product' : 'Name',
    value: isWeb ? 'all' : 'name',
  },
  {
    label: 'Best Seller',
    value: 'best-seller',
  },
  {
    label: 'New Arrival',
    value: 'new-arrival',
  },
];

const defaultSort = isWeb ? SORTS[0] : '';

const CatalogBrandScreen = (props) => {
  const dispatch = useDispatch();
  const paramsState = useSelector((state) => state.params);
  const productDetail = useSelector((state) => state.product.productData);
  const showBrandExportMenu = useSelector(
    (state) => state.modal.showBrandExportMenu,
  );

  const [totalData, setTotalData] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [brandDetail, setBrandDetail] = useState({});
  const [showDescription, setShowDescription] = useState(false);
  // const [loadingPagination, setLoadingPagination] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedSort, setSelectedSort] = useState(defaultSort);
  const [showSort, setShowSort] = useState(false);
  const [infoHeight, setInfoHeight] = useState(0);

  useEffect(() => {
    const mounted = true;
    if (mounted) {
      setSearchValue('');
      setLoading(true);
      setShowDescription(false);
      dispatch(paramsReseted());
      getDetail();
      setBrandDetail({});
      getSavedFilter('CatalogBrandScreen').then((res) => {
        const saved = res?.name?.['$regex'] || '';
        setSearchValue(saved);
        dispatch(paramsReseted());
        doSearch(saved, 0);
      });
    }
    return () => {
      dispatch(paramsReseted());
    };
  }, [props?.route?.params?.id]);

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      setLoading(true);
      doSearch();
    }
    return () => {
      mounted = false;
    };
  }, [selectedSort]);

  const getProducts = useApi(MsCatalog.getProducts);
  const getProductCount = useApi(MsCatalog.getProductCount);
  const getNewBestProducts = useApi(MsCatalog.getNewBestProducts);
  const getNewBestProductCount = useApi(MsCatalog.getNewBestProductCount);
  const getBrandDetail = useApi(MsCatalog.getBrandDetail);

  const convertBrandDescription = (description) => {
    return isWeb || showDescription || description?.length < 180
      ? description
      : `${description.substring(0, 180)}...`;
  };

  const handleExportCSV = async () => {
    try {
      const payload = props?.route?.params?.id
        ? { brand: { id: props.route.params.id } }
        : {};
      await exportCsvApi('product-export', payload, {
        isDownload: false,
        message: 'Export in progress, the result will be sent to your email.',
      });
    } catch (error) {
      console.log('error-export', error);
    }
  };

  const handleExportPdf = async () => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      await exportPdf('catalog', props?.route?.params?.id, props.navigation);
      dispatch(showPopup({ name: 'isLoading', value: false }));
    } catch (err) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
    }
  };

  const getDetail = async () => {
    try {
      const response = await getBrandDetail.request(props?.route?.params?.id);
      if (response?.data?.data?._id) {
        setBrandDetail(response.data.data);
      } else {
        setBrandDetail({});
      }
    } catch (e) {
      console.log(e);
    }
  };

  const doSearch = async (val = '', newSkip = null) => {
    const storeParams = JSON.parse(JSON.stringify(store.getState().params));
    const currentSkip =
      typeof newSkip === 'number' ? newSkip : storeParams.skip;
    const isLoadMore = currentSkip > 0 && !isWeb;
    try {
      setSearchValue(val);
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      const params = {
        params: {
          filter: {},
          skip: currentSkip,
          limit: storeParams.limit,
        },
      };
      if (props?.route?.params?.id) {
        params.params.filter['brand.id'] = props.route.params.id;
      }
      params.params.sort = 'name';
      const selectedFilter = selectedSort?.value;
      if (!isWeb && selectedFilter != 'name') {
        params.params.sort = selectedSort.value;
      }
      if (val) {
        if (['best-seller', 'new-arrival'].includes(selectedFilter)) {
          params.params.filter['product.name'] = { $regex: val, $options: 'i' };
        } else {
          params.params.filter['name'] = { $regex: val, $options: 'i' };
        }
      }
      // if (selectedFilter != isWeb ? 'all' : 'name') {
      //   if (isWeb) {
      //     if (selectedFilter == 'best-seller') {
      //       params.params.filter.is_best_seller = true;
      //     } else if (selectedFilter == 'new-arrival') {
      //       params.params.filter.is_new = true;
      //     }
      //   } else {
      //     params.params.sort = selectedSort.value;
      //   }
      // }

      if (['best-seller', 'new-arrival'].includes(selectedFilter)) {
        const productType =
          selectedFilter == 'best-seller' ? 'best_seller' : 'new_arrival';
        if (params?.params?.skip === 0) {
          const countResponse = await getNewBestProductCount.request(
            productType,
            params,
          );
          if (countResponse && countResponse.data && countResponse.data.data) {
            setTotalData(countResponse.data.data);
          } else {
            setTotalData(0);
          }
        }
        const response = await getNewBestProducts.request(productType, params);
        if (response?.data?.data?.length) {
          const result = response.data.data.map((pr) => {
            const converted = convertProductMarketType(pr);

            return {
              ...converted,
              categoryPath: getCategoryPath(converted),
            };
          });
          // setTotalData(result.length);
          if (isLoadMore) {
            const last_result = tableData.concat(result);
            setTableData(last_result);
          } else {
            setTableData(result);
          }
        } else {
          // setTotalData(0);
          if (!isLoadMore) {
            setTableData([]);
          }
        }
      } else {
        if (params?.params?.skip === 0) {
          const countResponse = await getProductCount.request(params);
          if (countResponse && countResponse.data && countResponse.data.data) {
            setTotalData(countResponse.data.data);
          } else {
            setTotalData(0);
          }
        }
        const response = await getProducts.request(params);
        if (response?.data?.data?.length) {
          const new_response = splitProductByCombination(
            response.data.data,
            true,
          ).map((pr) => {
            const converted = convertProductMarketType(pr);

            return {
              ...converted,
              categoryPath: getCategoryPath(converted),
            };
          });
          if (isLoadMore) {
            const last_result = tableData.concat(new_response);
            setTableData(last_result);
          } else {
            setTableData(new_response);
          }
        } else {
          if (!isLoadMore) {
            setTableData([]);
          }
        }
      }
      setLoading(false);
      if (isLoadMore) {
        setLoadingMore(false);
      }
    } catch (e) {
      setLoading(false);
      if (isLoadMore) {
        setLoadingMore(false);
      }
      console.log(e);
    }
  };

  const gotToDetail = (data) => {
    dispatch(setProductData(data));
  };

  const onCloseDetail = () => {
    dispatch(setProductData({}));
  };

  // const onLoadMore = () => {
  //   setLoadingPagination(true);
  //   setTimeout(async () => {
  //     const params = { params: JSON.parse(JSON.stringify(store.getState().params)) };
  //     if (props?.route?.params?.id) {
  //       params.params.filter['brand.id'] = props.route.params.id;
  //     }
  //     const response = await getProducts.request(params);
  //     if (response?.data?.data?.length) {
  //       const new_response = splitProductByCombination(response.data.data);
  //       setTableData(tableData.concat(new_response));
  //     }
  //     setLoadingPagination(false);
  //   }, 500);
  // };

  const onLayout = (e) => {
    if (e?.nativeEvent?.layout?.height) {
      const height = e.nativeEvent.layout.height;
      if (height != infoHeight) {
        setInfoHeight(e.nativeEvent.layout.height);
      }
    }
  };

  const BrandInfo = () => {
    return brandDetail?._id ? (
      <View style={styles.brandInfo} onLayout={onLayout}>
        <View style={styles.brandInfoLeft}>
          <View style={styles.brandInfoLogo}>
            <ImageContain source={brandDetail?.logo} width={55} height={55} />
          </View>
          <View style={styles.brandInfoDetail}>
            <SociollaText type="bold" style={styles.brandInfoName}>
              {brandDetail?.name ? brandDetail.name : '-'}
            </SociollaText>
            <View style={styles.brandInfoCountry}>
              <View style={styles.brandInfoCountryIcon}>
                <ImageContain
                  source={brandDetail?.flag}
                  width={16}
                  height={11}
                />
              </View>
              <SociollaText style={styles.brandInfoCountryName}>
                {brandDetail?.country ? firstLetter(brandDetail.country) : '-'}
              </SociollaText>
            </View>
            <SociollaText style={styles.brandInfoTotal}>
              {isVn()
                ? brandDetail?.total_product_b2b_vn
                : brandDetail?.total_product_b2b}{' '}
              Products
            </SociollaText>
            <SociollaText style={styles.brandType}>
              {isVn()
                ? capitalizeText(brandDetail?.collaboration_type_vn)
                : capitalizeText(brandDetail?.collaboration_type)}{' '}
              Brand
            </SociollaText>
            <SociollaText style={styles.brandStatus}>
              {isVn()
                ? capitalizeText(brandDetail?.collaboration_status_vn)
                : capitalizeText(brandDetail?.collaboration_status)}{' '}
            </SociollaText>
          </View>
        </View>
        <View style={styles.brandInfoRight}>
          <RenderHTML
            source={{
              html: brandDetail?.description
                ? convertBrandDescription(brandDetail.description)
                : '-',
            }}
            tagsStyles={{
              p: {
                color: Colors.black0,
                marginVertical: 0,
                textAlign: 'justify',
              },
            }}
          />
          {!isWeb &&
          brandDetail?.description &&
          brandDetail.description.toString().length > 180 ? (
            <TouchableOpacity
              activeOpacity={0.8}
              style={styles.seeMore}
              onPress={() => setShowDescription(!showDescription)}>
              <SociollaText style={styles.seeMoreText}>
                see {showDescription ? 'less' : 'more'}
              </SociollaText>
            </TouchableOpacity>
          ) : (
            false
          )}
        </View>
      </View>
    ) : (
      false
    );
  };

  return (
    <Layout.Container>
      {isWeb ? (
        <>
          {isWeb ? (
            <Layout.Back
              title="Product Catalog"
              onCallback={() => {
                props.navigation.navigate('CatalogScreen', { tab: '' });
              }}
            />
          ) : (
            false
          )}

          <BrandInfo />

          <Table
            name="catalog-product-brand"
            structures={tableStructure}
            data={tableData}
            loading={loading}
            hasCallback
            hasNoHide
            showFilter={false}
            totalData={totalData}
            skip={paramsState.skip}
            limit={paramsState.limit}
            searchKey="name"
            searchValue={searchValue}
            searchPlaceholder="Search by name..."
            onClickRow={({ data }) => gotToDetail(data)}
            onSearch={(val) => doSearch(val)}
            slot={{
              reference: ({ data, index }) => (
                <SociollaText
                  style={{ fontSize: 13 }}
                  key={`reference-${index}`}>
                  {data?.reference ? `[${data.reference}]` : '-'}
                </SociollaText>
              ),
              attribute: ({ data, index }) => (
                <View
                  style={{ flexDirection: 'row', flexWrap: 'wrap' }}
                  key={`slot-attribute-${index}`}>
                  {data.is_best_seller ? (
                    <ProductBadge status="best" style={{ marginLeft: 5 }} />
                  ) : (
                    false
                  )}
                  {data.is_promo ? (
                    <ProductBadge status="promo" style={{ marginLeft: 5 }} />
                  ) : (
                    false
                  )}
                  {data.is_new ? (
                    <ProductBadge status="new" style={{ marginLeft: 5 }} />
                  ) : (
                    false
                  )}
                  {!hasProductBadge(data) ? (
                    <SociollaText>-</SociollaText>
                  ) : (
                    false
                  )}
                </View>
              ),
            }}
            moreInlineFilter={
              <View>
                <SelectBox
                  label="Sort"
                  hideLabel
                  options={SORTS}
                  value={selectedSort}
                  placeholder="Select Sort"
                  trackBy="value"
                  labelKey="label"
                  isDropdown={isWeb}
                  onChange={async (value) => {
                    await dispatch(
                      paginationAdded({ skip: 0, limit: paramsState.limit }),
                    );
                    setSelectedSort(value);
                  }}
                />
              </View>
            }
          />
        </>
      ) : (
        <>
          <ListProductBrand
            name="catalog-product-brand"
            searchKey="name"
            placeholder="Search by name"
            loading={loading}
            onSearch={(value) => doSearch(value)}
            data={tableData}
            infoHeight={infoHeight}
            headerComponent={<BrandInfo />}
            onFilter={() => setShowSort(true)}
            hasFilter={Boolean(selectedSort)}
            withLoadMore
            withFetch
            loadingPagination={loadingMore}
            totalData={totalData}
            skip={paramsState.skip}
            limit={paramsState.limit}
            onLoadMore={(newSkip) => doSearch(searchValue, newSkip)}
            renderItem={({ data, index }) => (
              <ProductItemMobile
                data={data}
                onCallback={() => gotToDetail(data)}
                extraInfo={data.default_category?.name || ''}
                key={index}
              />
            )}
          />
          <ModalHalf
            title="Export"
            noTranslate={true}
            visible={showBrandExportMenu}
            onClose={() =>
              dispatch(showPopup({ name: 'showBrandExportMenu', value: false }))
            }
            maxWidth={'100%'}
            headerBordered>
            <TouchableOpacity
              style={styles.exportMenuItem}
              activeOpacity={0.8}
              onPress={() => {
                dispatch(
                  showPopup({ name: 'showBrandExportMenu', value: false }),
                );
                handleExportCSV();
              }}>
              <SociollaText style={styles.exportMenuText}>
                Export CSV
              </SociollaText>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.exportMenuItem}
              activeOpacity={0.8}
              onPress={() => {
                dispatch(
                  showPopup({ name: 'showBrandExportMenu', value: false }),
                );
                handleExportPdf();
              }}>
              <SociollaText style={styles.exportMenuText}>
                Export PDF
              </SociollaText>
            </TouchableOpacity>
          </ModalHalf>
          <ModalHalf
            title={'Sort'}
            visible={showSort}
            onClose={() => setShowSort(false)}
            maxWidth={'100%'}
            headerBordered>
            {SORTS.map((item, idx) => (
              <TouchableOpacity
                style={styles.optionItem}
                activeOpacity={0.8}
                key={idx}
                onPress={() => {
                  setSelectedSort(item);
                  setShowSort(false);
                  dispatch(
                    paginationAdded({ skip: 0, limit: paramsState.limit }),
                  );
                }}>
                <View style={styles.checklist}>
                  {item.value == selectedSort?.value ||
                  (!selectedSort?.value && item.value == 'name') ? (
                    <Image
                      style={CHECK_SIZE}
                      width={CHECK_SIZE.width}
                      height={CHECK_SIZE.height}
                      svg
                      source={require('@assets/img/icons/check-circle-blue.svg')}
                    />
                  ) : (
                    false
                  )}
                </View>
                <SociollaText>{item.label}</SociollaText>
              </TouchableOpacity>
            ))}
          </ModalHalf>
        </>
      )}
      <ModalDetailProduct
        back={!isWeb}
        withPromotion
        title="Product Detail"
        show={Boolean(productDetail && Object.keys(productDetail).length)}
        onClose={() => onCloseDetail()}
      />
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  brandInfo: {
    width: '100%',
    flexDirection: isWeb ? 'row' : 'column',
    marginBottom: isWeb ? 20 : 0,
  },
  brandInfoLeft: {
    width: isWeb ? 250 : fullWidth,
    flexDirection: 'row',
    // alignItems: 'center',
    justifyContent: 'space-between',
    borderRightColor: isWeb ? Colors.grey0 : Colors.transparent,
    borderRightWidth: isWeb ? 1 : 0,
    paddingRight: isWeb ? 20 : 10,
    ...(!isWeb
      ? {
          paddingTop: 15,
          paddingLeft: 10,
        }
      : {}),
  },
  brandInfoRight: {
    width: isWeb ? 'calc(100% - 250px)' : fullWidth,
    paddingLeft: isWeb ? 40 : 10,
    ...(!isWeb
      ? {
          paddingRight: 10,
          paddingTop: 15,
        }
      : {}),
  },
  brandInfoLogo: {
    width: 65,
    height: 65,
    borderRadius: 4,
    borderColor: Colors.grey0,
    borderWidth: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 4,
    overflow: 'hidden',
    backgroundColor: Colors.white,
  },
  brandInfoDetail: {
    width: isWeb ? 'calc(100% - 75px)' : fullWidth - 105,
    marginTop: 5,
  },
  brandInfoName: {},
  brandInfoCountry: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 4,
  },
  brandInfoCountryIcon: {
    marginTop: -3,
    marginRight: 5,
  },
  brandInfoCountryName: {
    color: Colors.black0,
  },
  brandInfoTotal: {
    color: Colors.black0,
  },
  brandType: {
    color: Colors.grey1,
  },
  brandStatus: {
    color: Colors.grey1,
  },
  optionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 15,
    borderBottomColor: Colors.grey0,
    borderBottomWidth: 1,
  },
  checklist: {
    width: CHECK_SIZE.width + 1,
    height: CHECK_SIZE.width + 1,
    borderRadius: CHECK_SIZE.width + 1,
    borderColor: Colors.grey0,
    borderWidth: 1,
    marginRight: 15,
  },
  seeMore: { marginTop: 7 },
  seeMoreText: { color: Colors.blue0 },
  exportMenuItem: {
    paddingVertical: 14,
    paddingHorizontal: 15,
    borderBottomColor: Colors.grey0,
    borderBottomWidth: 1,
  },
  exportMenuText: {
    fontSize: 14,
  },
});

export default CatalogBrandScreen;
